/**
 * Backbone model which describes a generic user operation.
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import BoundOperation from "core/models/BoundOperation";
/**
 * Backbone model which describes a generic user operation.
 */
var Operation = Backbone.Model.extend(
  {
    defaults: {
      text: "Default Text",
      icon: "",
    },

    initialize: function (attributes, options) {
      var action = attributes.action,
        available = attributes.available;

      if (_.isFunction(action)) {
        this.action = _.bind(function (model, options) {
          this.trigger("before" + this.id, model, options);
          return action.apply(this, arguments).done(
            _.bind(function () {
              this.trigger(this.id, model, options);
              this.trigger("after" + this.id, model, options);
            }, this)
          );
        }, this);
        delete attributes.action;
      }

      if (_.isFunction(available)) {
        this.available = _.bind(available, this);
        delete attributes.available;
      }

      Backbone.Model.prototype.initialize.apply(this, arguments);
    },

    /**
     * Execute the operation. By default, this finishes immediately with `model`.
     * @param  model   The model associated with the operation.
     * @param  {Object} [options]   The options associated with the operation.
     * @return {Promise}         A promise object which finishes with the replacement model (may be the same) and an optional message or fails with an error or `false` if the operation was cancelled.
     */
    action: function (model, options) {
      var self = this;

      this.trigger("before" + this.id, model, options);
      this.trigger(this.id, model, options);

      return Operation.resolve(model).done(function (model) {
        self.trigger(this.id, model, options);
        self.trigger("after" + this.id, model, options);
      });
    },

    /**
     * Determine if the operation is available in the current session for the given model. By default, this returns `true`.
     * @param  model   The model associated with the operation.
     * @return {Boolean}         True if the operation is available, otherwise false.
     */
    available: function (model) {
      return model ? true : false;
    },

    /**
     * Binds the `action` and `available` functions in such a way that they execute properly with no arguments or a single model argument.
     * @param  {Function} [modelGetter] A function which returns the model to supply to `action` and `available`. By default, the model supplied to the BoundOperation will be used.
     * @param  {Function} [success]     A function to execute on success of `action`.
     * @param  {Function} [error]       A function to execute on error of `action`.
     * @return {BoundOperation}             This object, with wrapped `action` and `available` functions.
     */
    bind: function (modelGetter, success, error) {
      var action = _.bind(this.action, this);
      var available = _.bind(this.available, this);

      return new BoundOperation(
        _.extend({}, this.attributes, {
          action: function (model, options) {
            if (!model && _.isFunction(modelGetter)) {
              model = modelGetter();
            }

            var result = action(model, options);

            if (_.isFunction(success)) {
              result = result.done(success);
            }

            if (_.isFunction(error)) {
              result = result.fail(error);
            }

            return result;
          },
          available: function (model) {
            if (!model && _.isFunction(modelGetter)) {
              model = modelGetter();
            }

            return available(model);
          },
        }),
        {
          parent: this,
        }
      );
    },

    /**
     * Unbinds the `action` and `available` functions.
     * @return {Operation} This object, with unbound `action` and `available` functions.
     */
    unbind: function () {
      return this;
    },
  },
  {
    /**
     * Gets a promise which has been resolved with the given argument.
     * @param  {mixed} model The model with which to resolve.
     * @param {String} [msg] The message with which to resolve, if any.
     * @return {Promise}       A promise which has been resolved with the given argument.
     */
    resolve: function (model, msg) {
      var deferred = new $.Deferred();

      deferred.resolve(model, msg);

      return deferred.promise();
    },

    /**
     * Gets a promise which has been failed with the given argument.
     * @param  {mixed} err The error with which to reject.
     * @return {Promise}     A promise which has been failed with the given argument.
     */
    reject: function (err) {
      var deferred = new $.Deferred();

      deferred.reject(err);

      return deferred.promise();
    },
  }
);

export default Operation;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/models/Operation.js
// module id = 32
// module chunks = 1